<template>
  <div class="ui-jobsearch">
    <div class="ui-jobsearch__prompt">{{ block.props.prompt }}</div>
    <input
      v-if="interactive && !block.props.hide_keyword"
      v-model="query"
      type="text"
      class="ui-field-input"
      :placeholder="block.props.placeholder || 'Search jobs…'"
      @input="onInput"
      @keyup.enter="manualMode && runManualSearch()"
    />
    <div v-if="interactive && searchFields.length" class="ui-jobsearch__filters">
      <div
        v-for="field in searchFields"
        :key="field.attr"
        class="ui-jobsearch__filter"
        :class="{ 'ui-jobsearch__filter--wide': field.type === 'tree' || field.variant === 'pills' }"
      >
        <label :for="`uijs-${block.block_id}-${field.attr}`" class="ui-field-label">
          {{ field.label }}<span v-if="field.required" class="ui-jobsearch__req">required</span>
        </label>
        <!--
          Pills variant (engine-capped to small value sets): one
          toggleable pill per option — click again to clear, since a
          filter is "any" when empty.
        -->
        <div
          v-if="field.type === 'select' && field.variant === 'pills'"
          class="ui-pills"
          role="group"
          :aria-label="field.label"
        >
          <button
            v-for="opt in field.options"
            :key="opt"
            type="button"
            class="ui-pill"
            :class="{ 'ui-pill--on': fieldValues[field.attr] === opt }"
            :aria-pressed="fieldValues[field.attr] === opt ? 'true' : 'false'"
            @click="togglePill(field, opt)"
          >{{ opt }}</button>
        </div>
        <select
          v-else-if="field.type === 'select'"
          :id="`uijs-${block.block_id}-${field.attr}`"
          v-model="fieldValues[field.attr]"
          class="ui-field-input ui-field-select"
        >
          <!--
            Placeholder prompt: shown in the collapsed control while the
            value is empty, but hidden from the open list so it never
            reads as a pickable value; '— any —' is the clear entry.
          -->
          <option v-if="field.placeholder" value="" hidden>{{ field.placeholder }}</option>
          <option value="">— any —</option>
          <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <!-- The picker owns selection display AND clearing (the ✕ on its
             control) — no separate "Selected:" line. `initial` seeds a
             pre-filled criteria value into the control. -->
        <UiBlockTreePicker
          v-else-if="field.type === 'tree'"
          :tree="field.tree"
          :initial="treeInitial(field)"
          :free-text="field.free_text === true"
          :placeholder="field.placeholder || (field.free_text ? 'Type a value or pick from the list' : '— select —')"
          @pick="fieldValues[field.attr] = $event.value"
          @clear="fieldValues[field.attr] = ''"
        />
        <input
          v-else
          :id="`uijs-${block.block_id}-${field.attr}`"
          v-model="fieldValues[field.attr]"
          type="text"
          class="ui-field-input"
          :placeholder="field.placeholder || ''"
          @keyup.enter="runManualSearch"
        />
      </div>
    </div>
    <button
      v-if="interactive && manualMode"
      type="button"
      class="ui-primary-btn ui-jobsearch__searchbtn"
      @click="runManualSearch"
    >Search</button>
    <div v-if="!interactive" class="ui-jobsearch__inert">
      <template v-if="chosenLabel">Selected: {{ chosenLabel }}</template>
      <template v-else>This step is no longer active.</template>
    </div>
    <div v-if="status" class="ui-jobsearch__status">{{ status }}</div>
    <div v-if="error" class="ui-jobsearch__error">{{ error }}</div>
    <!-- Result rows (DS MatchItem anatomy): title + other-hierarchy
         context on the top line, status dot on the second. The RM
         reference is deliberately not shown — picking a row carries it.
         The main hierarchy lives in the family group headings. -->
    <div v-if="interactive && results.length > 0" class="ui-jobsearch__results">
      <template v-for="row in resultRows">
        <div
          v-if="row.type === 'header'"
          :key="`family-${row.family}`"
          class="ui-group-label"
        >{{ row.family }}<span
          v-if="row.description"
          class="ui-group-label__desc"
        > — {{ row.description }}</span></div>
        <!-- div role=button rather than <button>: the "Why it matches"
             disclosure nests its own real button inside the row. -->
        <div
          v-else
          :key="row.doc.doc_id"
          class="ui-jobsearch__result"
          role="button"
          tabindex="0"
          @click="pick(row.doc)"
          @keyup.enter.self="pick(row.doc)"
        >
          <span class="ui-jobsearch__main">
            <span class="ui-jobsearch__name">
              <span class="ui-jobsearch__name-text">{{ row.doc.name }}</span>
              <span
                v-if="row.doc.other_hierarchies && row.doc.other_hierarchies.length"
                class="ui-jobsearch__ctx"
              >{{ row.doc.other_hierarchies.join(' · ') }}</span>
            </span>
            <span v-if="row.doc.status" class="ui-jobsearch__sub">
              <span
                class="ui-jobsearch__dot"
                :style="{ background: statusColor(row.doc.status) }"
                aria-hidden="true"
              />
              <span>{{ row.doc.status }}</span>
            </span>
            <ul
              v-if="row.doc.match_reasons && row.doc.match_reasons.length && isWhyOpen(row.doc)"
              class="ui-why-list"
            >
              <li
                v-for="(reason, ri) in row.doc.match_reasons"
                :key="ri"
                class="ui-why-item"
              >
                <v-icon size="13" class="ui-why-check">mdi-check</v-icon>
                <span>{{ reason }}</span>
              </li>
            </ul>
          </span>
          <!-- Trailing column: match pill with the "Why it matches"
               toggle beneath it. The toggle never fires the row. -->
          <span
            v-if="row.doc.match || (row.doc.match_reasons && row.doc.match_reasons.length)"
            class="ui-jobsearch__trail"
          >
            <span
              v-if="row.doc.match"
              class="ui-match"
              :class="`ui-match--${row.doc.match}`"
              :title="matchTitle(row.doc.match)"
            >{{ matchLabel(row.doc.match) }}</span>
            <button
              v-if="row.doc.match_reasons && row.doc.match_reasons.length"
              type="button"
              class="ui-why-toggle"
              :class="{ 'ui-why-toggle--open': isWhyOpen(row.doc) }"
              :aria-expanded="isWhyOpen(row.doc) ? 'true' : 'false'"
              @click.stop="toggleWhy(row.doc)"
              @keyup.enter.stop
            >
              <v-icon size="13">mdi-help-circle-outline</v-icon>
              Why it matches
            </button>
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, reactive, ref } from 'vue';
import axios from 'axios';
import {
  docStatusColor,
  groupDocRowsByFamily,
  matchTierLabel,
  matchTierTitle,
} from '@rm-ui/agent/uiBlockSpec';
import UiBlockTreePicker from './UiBlockTreePicker.vue';

/** Results shown per search — server caps at 200; the card wants a scannable list. */
const MAX_RESULTS = 8;
const DEBOUNCE_MS = 350;
const MIN_QUERY_CHARS = 2; // search_jobs_by_name rejects 1-char queries

/**
 * `job_search` ui_block — search the catalogue inside the card, pick a
 * document. The search runs through the same curated agent tool the
 * model uses (`search_jobs_by_name`) via the per-surface execute
 * endpoint, so tenancy and client search defaults apply identically.
 * Picking emits `action` with `{ key: 'selected', label, value }` where
 * value is the RM reference — recorded by the engine in
 * `values[step_key]`.
 */
export default defineComponent({
  name: 'UiBlockJobSearch',
  components: { UiBlockTreePicker },
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    /** Friendly label of the recorded selection, for inert replay cards. */
    chosenLabel: { type: String, default: null },
  },
  emits: ['action'],
  setup(props, { emit }) {
    // Previously recorded criteria — present when the engine re-emitted
    // this search because the user came BACK to it ("Search again" /
    // no-results bounce). Seed the inputs so nothing is retyped.
    const initial =
      props.block.props.initial_criteria &&
      typeof props.block.props.initial_criteria === 'object'
        ? props.block.props.initial_criteria
        : {};

    const query = ref(typeof initial.query === 'string' ? initial.query : '');
    const results = ref([]);
    // Grouped display rows: family headings appear whenever the engine
    // supplied families (a single family included) — the heading is
    // where the main hierarchy lives; rows carry status + context only.
    const resultRows = computed(() => groupDocRowsByFamily(results.value));
    const status = ref('');
    const error = ref('');
    let debounceTimer = null;
    let searchSeq = 0;

    // Authored extra filter inputs (engine-resolved labels) and the
    // configured doc-type restriction. Field values are seeded up front
    // (Vue 2.7 reactive() can't detect key additions).
    const searchFields = Array.isArray(props.block.props.search_fields)
      ? props.block.props.search_fields
      : [];
    const fieldSeed = {};
    for (const field of searchFields) fieldSeed[field.attr] = '';
    if (Array.isArray(initial.field_filters)) {
      for (const filter of initial.field_filters) {
        if (
          filter &&
          typeof filter.attr === 'string' &&
          filter.attr in fieldSeed &&
          Array.isArray(filter.values) &&
          filter.values.length
        ) {
          fieldSeed[filter.attr] = `${filter.values[0]}`;
        }
      }
    }
    const fieldValues = reactive(fieldSeed);

    function buildFieldFilters() {
      const filters = [];
      for (const field of searchFields) {
        const v = `${fieldValues[field.attr] ?? ''}`.trim();
        if (v) filters.push({ attr: field.attr, values: [v] });
      }
      return filters;
    }

    function missingRequired() {
      return searchFields.filter(
        (f) => f.required && !`${fieldValues[f.attr] ?? ''}`.trim(),
      );
    }

    // next_step mode: the card only COLLECTS criteria — Search advances
    // the workflow with trigger `searched` and the criteria object, and
    // a following doc_list step displays the results.
    const passThrough = props.block.props.results === 'next_step';

    // With configured filter fields (or in pass-through mode) the card
    // searches ON DEMAND (the Search button / Enter) instead of
    // as-you-type — multi-field entry shouldn't fire a query per
    // keystroke, and advancing a workflow must be an explicit action.
    const manualMode = searchFields.length > 0 || passThrough;

    function onInput() {
      if (manualMode) {
        error.value = '';
        return;
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      error.value = '';
      const q = query.value.trim();
      if (q.length < MIN_QUERY_CHARS) {
        results.value = [];
        status.value = '';
        return;
      }
      debounceTimer = setTimeout(() => search(q), DEBOUNCE_MS);
    }

    function runManualSearch() {
      if (!manualMode) return;
      error.value = '';
      const q = query.value.trim();
      const missing = missingRequired();
      if (missing.length) {
        results.value = [];
        status.value = `Fill in ${missing.map((f) => f.label).join(', ')} to search.`;
        return;
      }
      if (q.length > 0 && q.length < MIN_QUERY_CHARS) {
        status.value = 'The keyword needs at least 2 characters.';
        return;
      }
      if (q.length < MIN_QUERY_CHARS && !buildFieldFilters().length) {
        status.value = 'Enter a keyword or fill in a search field.';
        return;
      }
      if (passThrough) {
        advanceWithCriteria(q);
        return;
      }
      search(q);
    }

    /** next_step mode: hand the criteria to the workflow — the engine
     * runs the search and the FOLLOWING step displays the results. */
    function advanceWithCriteria(q) {
      const docTypes = Array.isArray(props.block.props.document_types)
        ? props.block.props.document_types
        : [];
      const fieldFilters = buildFieldFilters();
      const summary = [];
      if (q) summary.push(`keyword "${q}"`);
      for (const filter of fieldFilters) {
        const field = searchFields.find((f) => f.attr === filter.attr);
        summary.push(`${field ? field.label : filter.attr}: ${filter.values.join(', ')}`);
      }
      emit('action', {
        key: 'searched',
        label: `Search for ${summary.join(', ') || 'everything'}`,
        value: {
          ...(q ? { query: q } : {}),
          ...(docTypes.length ? { document_types: docTypes } : {}),
          ...(fieldFilters.length ? { field_filters: fieldFilters } : {}),
        },
      });
    }

    async function search(q) {
      const seq = ++searchSeq;
      status.value = 'Searching…';
      const docTypes = Array.isArray(props.block.props.document_types)
        ? props.block.props.document_types
        : [];
      const fieldFilters = buildFieldFilters();
      try {
        const resp = await axios.post(
          '/agent/tools/search_jobs_by_name/execute',
          {
            args: {
              query: q.length >= MIN_QUERY_CHARS ? q : undefined,
              ...(docTypes.length ? { document_types: docTypes } : {}),
              ...(fieldFilters.length ? { field_filters: fieldFilters } : {}),
            },
          },
          { headers: { 'X-Atlas-Surface': 'portal' } },
        );
        if (seq !== searchSeq) return; // superseded by a newer keystroke
        const data = resp.data?.data ?? {};
        const docs = Array.isArray(data.documents) ? data.documents : [];
        results.value = docs.slice(0, MAX_RESULTS);
        if (!docs.length) {
          status.value = 'No matches — try different keywords.';
        } else if (data.suggested_for) {
          // Nothing matched the words typed; these are similar roles the
          // engine found by meaning (see the tool's suggested_for).
          status.value =
            `No role is named “${data.suggested_for}” — showing the closest existing roles.`;
        } else if (data.totalCount > MAX_RESULTS) {
          status.value = `Showing ${results.value.length} of ${data.totalCount} — keep typing to narrow.`;
        } else {
          status.value = '';
        }
      } catch (err) {
        if (seq !== searchSeq) return;
        results.value = [];
        status.value = '';
        error.value = 'Search failed — please try again.';
      }
    }

    /** Pills are single-pick filters: picking the active pill clears it
     * back to "any". */
    function togglePill(field, opt) {
      fieldValues[field.attr] = fieldValues[field.attr] === opt ? '' : opt;
    }

    /** Seed a pre-filled tree criteria value into the picker's control.
     * Criteria only record the picked VALUE (no path), so the label is
     * the bare leaf name. Read once at setup — pickers mount with the
     * card. */
    function treeInitial(field) {
      const v = `${fieldValues[field.attr] ?? ''}`.trim();
      return v ? { value: v, path: [v] } : null;
    }

    function pick(doc) {
      if (!props.interactive) return;
      emit('action', {
        key: 'selected',
        label: `I chose: ${doc.name} (${doc.reference})`,
        value: doc.reference,
      });
    }

    // "Why it matches" disclosure state, keyed per row. Reassigned (not
    // mutated) so Vue 2.7's reactivity sees new keys.
    const whyOpen = ref({});
    function whyKey(doc) {
      return String(doc.doc_id ?? doc.reference);
    }
    function toggleWhy(doc) {
      const k = whyKey(doc);
      whyOpen.value = { ...whyOpen.value, [k]: !whyOpen.value[k] };
    }
    function isWhyOpen(doc) {
      return !!whyOpen.value[whyKey(doc)];
    }

    return {
      query,
      results,
      resultRows,
      status,
      error,
      onInput,
      pick,
      searchFields,
      fieldValues,
      manualMode,
      runManualSearch,
      togglePill,
      treeInitial,
      // Pill text + the hover reason for its band (shared copy).
      matchLabel: matchTierLabel,
      matchTitle: matchTierTitle,
      // Status-dot colour (shared prototype STATUS_COLOR mapping).
      statusColor: docStatusColor,
      // "Why it matches" disclosure.
      toggleWhy,
      isWhyOpen,
    };
  },
});
</script>

<style scoped>
/* Prototype Surface treatment (DS MatchesCard): quiet white card —
   hairline grey border, 16px radius, whisper of a shadow. */
.ui-jobsearch {
  margin-top: 12px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.9);
  background: white;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
/* Card heading (DS Eyebrow): small, semibold, quiet grey. */
.ui-jobsearch__prompt {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(156 163 175);
  margin-bottom: 12px;
}
.ui-jobsearch__inert {
  font-size: 12px;
  color: rgb(107 114 128);
}
/* Prototype field grid rhythm: 20px column gap, 16px row gap. */
.ui-jobsearch__filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px 20px;
  margin-top: 12px;
}
/* "required" tag beside the label (DS RoleFormCard), not a red star. */
.ui-jobsearch__req {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: rgb(99 102 241);
}
/* Layout only — the button look comes from .ui-primary-btn (shared). */
.ui-jobsearch__searchbtn {
  margin-top: 16px;
}
.ui-jobsearch__filter--wide {
  grid-column: 1 / -1;
}
.ui-jobsearch__status {
  margin-top: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgb(107 114 128);
}
.ui-jobsearch__error {
  margin-top: 8px;
  font-size: 12px;
  color: rgb(185 28 28);
}
/* Results as a divided list (DS MatchItem rows), not bordered boxes —
   the card supplies the containment. */
.ui-jobsearch__results {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}
.ui-jobsearch__result {
  display: flex;
  /* Top-aligned: the trail column (pill + why) sits level with the
     title line even when the reasons list expands the row. */
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 12px 6px;
  border: 0;
  border-bottom: 1px solid rgb(243 244 246);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease;
}
.ui-jobsearch__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
/* Trailing column: match pill on top, "Why it matches" beneath it,
   right-aligned and level with the title line. */
.ui-jobsearch__trail {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-top: 2px;
}
.ui-jobsearch__result:last-child {
  border-bottom: 0;
}
.ui-jobsearch__result:hover {
  background: rgb(249 250 251);
}
/* Top line: title, other-hierarchy context, match pill right-aligned. */
.ui-jobsearch__name {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.ui-jobsearch__name-text {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgb(17 24 39);
}
.ui-jobsearch__ctx {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 12px;
  color: rgb(156 163 175);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Second line: status dot + status. */
.ui-jobsearch__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  color: rgb(107 114 128);
}
.ui-jobsearch__dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
}
</style>

<!-- Shared pill/button look — edit uiBlockShared.css, not the scoped block above. -->
<style src="./uiBlockShared.css"></style>
